<template>
    <div id="myChartBook">
        <div v-if="!showAddEditForm" class="mt-4 w-full">
            <template v-if="!isEditMode">
                <div v-if="!hasChartbooks()">
                    <div class="inline-flex mt-2 print:hidden" v-if="!jims">
                        <span class="btn btn-blue-500 btn-sm" @click="showAddEditForm = true">
                            Add or Edit Chart Books
                        </span>
                    </div>
                    <alert-box>
                        <template #body>
                            You do not have any chartbooks.
                        </template>
                    </alert-box>
                </div>
                <div v-else class="flex flex-wrap items-center">
                    <label for="chartbook-select" class="text-uppercase font-bold mr-2 mt-2 text-sm xs:w-full">Book:</label>
                    <div class="inline-block sm:mt-2 xs:w-full">
                        <select id="chartbook-select" v-model="selectedChartbook" class="form-control form-control-sm align-bottom bg-white">
                            <option :value="pChartbook" v-for="pChartbook in chartbooks" :key="pChartbook">
                                {{ pChartbook.name }}
                            </option>
                        </select>
                    </div>
                    <div class="inline-flex mt-2 xs:w-full xs:mt-3 ml-auto print:hidden" v-if="!jims">
                        <span class="btn btn-blue-500 btn-sm inline-block xs:w-full" @click="showAddEditForm = true">
                            Add or Edit Chart Books
                        </span>
                    </div>
                </div>
            </template>

            <div v-if="hasChartbooks()" class="mt-3 py-1" :class="{'border-t' : !isEditMode}">
                <div>
                    <my-chart-book-table
                        @editMode="editMode"
                        @saved="updateChartbook"
                        @addedChart="addedChart"
                        @movedChart="movedChart"
                        @updatedChart="updateChart"
                        @removedChart="removeChart"
                        :jims="jims"
                        :initial-chartbook="selectedChartbook"
                        :initial-chartbooks="chartbooks"
                    >
                    </my-chart-book-table>
                </div>
            </div>
        </div>
        <div v-else>
            <my-chart-book-form @save="updateChartbooks" @close="showAddEditForm = false" :initial-chartbooks="chartbooks"></my-chart-book-form>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import MyChartBookTable from './MyChartBook/MyChartBookTable';
import MyChartBookForm from './MyChartBook/MyChartBookForm';

const props = defineProps({
    initialChartbooks: {
        type: Object,
    },
    jims: {
        type: Boolean,
    },
});

const chartbooks = ref(props.initialChartbooks || []);
const selectedChartbook = ref(findLastViewedChartbook() || props.initialChartbooks[0]);
const showAddEditForm = ref(props.initialChartbooks.length === 0 ? true : false);
const isEditMode = ref(false);

function editMode(status) {
    isEditMode.value = status;
}

function findLastViewedChartbook() {
    const last = window.localStorage.getItem('lastViewedChartbook');
    if (last) {
        return chartbooks.value.find(c => c.id === parseInt(last));
    }
}

function hasChartbooks() {
    return chartbooks.value.length > 0;
}

function updateChartbook(newChartbook) {
    const index = chartbooks.value.findIndex(c => c.id == newChartbook.id);
    chartbooks.value.splice(index, 1, newChartbook);
    selectedChartbook.value = newChartbook;
}

function addedChart(chartbookId, chart) {
    const selectedChartbook = chartbooks.value.find(c => c.id == chartbookId);
    selectedChartbook.charts.push(chart);
}

function updateChart(chartbookId, chart) {
    const chartbook = chartbooks.value.find(c => c.id === chartbookId);
    const chartIndex = chartbook.charts.findIndex(c => c.id == chart.id);
    chartbook.charts.splice(chartIndex, 1, chart);
}

function removeChart(chartbookId, chart) {
    const chartbook = chartbooks.value.find(c => c.id === chartbookId);
    const chartIndex = chartbook.charts.findIndex(c => c.id == chart.id);
    chartbook.charts.splice(chartIndex, 1);
}

function movedChart(oldChartbookId, newChartbookId, chart) {
    const oldChartbook = chartbooks.value.find(c => c.id == oldChartbookId);
    const newChartbook = chartbooks.value.find(c => c.id == newChartbookId);
    const oldChartIndex = oldChartbook.charts.findIndex(c => c.id == chart.id);
    newChartbook.charts.push(chart);
    oldChartbook.charts.splice(oldChartIndex, 1);
}

function updateChartbooks(updatedChartbooks) {
    const existingChartBookIds = chartbooks.value.map(c => c.id);
    const newChartBooks = updatedChartbooks.filter(c => !existingChartBookIds.includes(c.id));
    chartbooks.value = updatedChartbooks;
    showAddEditForm.value = false;
    if (newChartBooks.length > 0) {
        selectedChartbook.value = newChartBooks[0];
    } else {
        selectedChartbook.value = updatedChartbooks[0];
    }
}

// Watcher for selectedChartbook
watch(selectedChartbook, (chartBook) => {
    if (!chartBook) {
        window.localStorage.removeItem('lastViewedChartbook');
    } else {
        window.localStorage.setItem('lastViewedChartbook', chartBook.id);
    }
});
</script>

<style lang="scss">
#myChartBook {
    table {
        thead {
            td, th {
                @apply px-3 py-2;
            }
        }
        tbody {
            td, th {
                @apply px-3;
            }
        }
    }
}
</style>
